import React from 'react';
import SimpleReportForm from '../SimpleReportForm';
import CibilReportOutputComponent from '../output/CibilReportOutputComponent';

const GENDERS = [
    { label: 'Male', value: 'male' },
    { label: 'Female', value: 'female' },
];

/**
 * Props (all optional — when omitted, SimpleReportForm falls back to the
 * URL-param flow used by the standalone /reports/surepass-cibil-report/:id
 * route, so existing usage from BorrowerReports keeps working):
 *
 *   serviceId        — pass when embedded (Lead Wizard smart-prefill drawer).
 *   serviceDetails   — pricing/balance bag from the by-code lookup; lets the
 *                      banner render credits without an extra fetch.
 *   leadId           — when set, the saved BureauBoxReport row gets bound to
 *                      this lead via the model's creating hook.
 *   initialValues    — pre-fill for the form fields (mobile, pan, name,
 *                      gender). Used by the wizard so the DSA doesn't retype.
 */
const CibilReportInputComponent = ({
    serviceId,
    serviceDetails,
    leadId,
    initialValues,
    // Cached/rehydrated report — if passed, the form opens in OUTPUT mode
    // (showing the saved score + details) instead of the input form. Avoids
    // the ~₹120 re-fetch cost when re-opening the drawer for a lead that
    // already has a stored CIBIL report.
    initialReportData,
} = {}) => (
    <SimpleReportForm
        serviceId={serviceId}
        serviceDetails={serviceDetails}
        leadId={leadId}
        initialValues={initialValues}
        initialReportData={initialReportData}
        banner={{ icon: 'pi-chart-line', title: 'CIBIL Credit Report', subtitle: 'Fetch CIBIL credit report by mobile + PAN' }}
        fields={[
            { name: 'mobile', label: 'Mobile Number', icon: 'pi-phone', placeholder: '9876543210', maxLength: 10, inputMode: 'tel', numeric: true, mono: true,
              regex: /^[6-9]\d{9}$/, regexError: 'Enter a valid 10-digit Indian mobile' },
            { name: 'pan', label: 'PAN Number', icon: 'pi-id-card', placeholder: 'ABCDE1234F', maxLength: 10, autoCapitalize: 'characters', spellCheck: false, uppercase: true, mono: true,
              regex: /^[A-Z]{5}\d{4}[A-Z]$/, regexError: 'Invalid PAN format', bodyKey: 'pan' },
            { name: 'name', label: 'Full Name', icon: 'pi-user', placeholder: 'Enter full name', maxLength: 100, autoCapitalize: 'words' },
            { name: 'gender', label: 'Gender', type: 'dropdown', options: GENDERS, placeholder: 'Select gender', colClass: 'col-12 md:col-6 lg:col-3' },
        ]}
        endpoint={{
            path: '/surepass/credit-report-cibil/fetch-report',
            bodyMapper: (v, sid) => ({ mobile: v.mobile, pan: v.pan, name: v.name.trim(), gender: v.gender, consent: v.consent ? 'Y' : 'N', api_provider_service_id: parseInt(sid, 10) }),
        }}
        consentReportType="cibil-report"
        otpGate
        otpMobileField="mobile"
        submitLabel="Fetch CIBIL Report"
        successSummary="CIBIL report fetched"
        errorSummary="CIBIL report failed"
        confirmHeader="Confirm CIBIL report fetch"
        confirmAcceptLabel="Yes, fetch"
        // Renders the PDF from the report we already paid for, so re-downloading
        // is free. Deliberately NOT the `fetch-report-pdf` endpoint — that buys
        // TransUnion's own branded CIR and charges credits on every click.
        pdfEndpoint={(reportId) => `/surepass/credit-report-cibil/${reportId}/generate-pdf`}
        pdfPasswordHint='Last 5 characters of the PAN + "PAN" (Example: if PAN is ABCDE1234F, password is 1234FPAN)'
        outputComponent={CibilReportOutputComponent}
    />
);

export default CibilReportInputComponent;
